import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Rule, type Agent, type Skill } from "../lib/api";

type Tab = "rules" | "agents" | "skills";

export default function Governance() {
  const [tab, setTab] = useState<Tab>("rules");
  const [rules, setRules] = useState<Rule[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api<{ rules: Rule[] }>("/governance/rules"),
      api<{ agents: Agent[] }>("/governance/agents"),
      api<{ skills: Skill[] }>("/governance/skills"),
    ]).then(([r, a, s]) => {
      setRules(r.rules); setAgents(a.agents); setSkills(s.skills);
      setLoading(false);
    });
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Governance</h1>
        <p className="text-sm text-gray-500 mt-1">D1-canonical rules + agents + skills</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {(["rules", "agents", "skills"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t
                ? "border-growtify-500 text-growtify-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t} ({t === "rules" ? rules.length : t === "agents" ? agents.length : skills.length})
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="Filter…"
        value={filter}
        onChange={(e) => setFilter(e.target.value.toLowerCase())}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-growtify-500 focus:outline-none focus:ring-1 focus:ring-growtify-500"
      />

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading governance…</div>
      ) : tab === "rules" ? (
        <RuleList rules={rules.filter((r) => r.name.toLowerCase().includes(filter))} />
      ) : tab === "agents" ? (
        <AgentList agents={agents.filter((a) => a.name.toLowerCase().includes(filter))} />
      ) : (
        <SkillList skills={skills.filter((s) => s.name.toLowerCase().includes(filter))} />
      )}
    </div>
  );
}

function RuleList({ rules }: { rules: Rule[] }) {
  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
      {rules.map((r) => (
        <li key={r.id} className="px-4 py-3 hover:bg-gray-50">
          <Link to={`/governance/rules/${r.name}`} className="block">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">{r.name}</span>
              <span className="text-xs text-gray-500">v{r.version} · {r.scope}</span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function AgentList({ agents }: { agents: Agent[] }) {
  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
      {agents.map((a) => (
        <li key={a.id} className="px-4 py-3 hover:bg-gray-50">
          <Link to={`/governance/agents/${a.name}`} className="block">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">{a.name}</span>
              <span className="text-xs text-gray-500">
                R{a.authority_rank} · {a.model_default}
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SkillList({ skills }: { skills: Skill[] }) {
  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
      {skills.map((s) => (
        <li key={s.id} className="px-4 py-3 hover:bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900">{s.invoke_pattern}</span>
            <span className="text-xs text-gray-500">{s.owning_agent ?? "no owner"}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
